"use client";

import React from "react";
import { Calendar, Clock, ChevronLeft, Tag, User, ExternalLink } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";


declare global {
  interface Window {
    wvcClient: any;
  }
}


const getWvcClient = () => {
  if (typeof window !== "undefined" && window.wvcClient) {
    return window.wvcClient;
  }
  return {
    getCurrentWPQueryParams: () => ({ post_type: "post" }),
  };
};

function VideoEmbed({ videoUrl }: { videoUrl: string }) {
  const getEmbedUrl = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
  };

  const embedUrl = getEmbedUrl(videoUrl);
  if (!embedUrl) return null;

  return (
    <div className="my-12 aspect-video w-full overflow-hidden rounded-md shadow-lg border border-border bg-black">
      <iframe
        src={embedUrl}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Repair Tutorial Video"
      />
    </div>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        {[...Array(3)].map((_, i) => (
          <Card key={i} data-index={i} className="animate-pulse bg-muted/20 border-border/50 rounded-md">
            <CardHeader className="h-40 bg-muted/30 rounded-t-md" />
            <CardContent className="p-6">
              <div className="h-6 bg-muted/40 rounded-md w-3/4 mb-4" />
              <div className="h-4 bg-muted/40 rounded-md w-full mb-2" />
              <div className="h-4 bg-muted/40 rounded-md w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  return (
    <div className="mt-16">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-bold font-default text-foreground">More Repair Guides</h3>
        <Link to="#">
          <Button variant="ghost" className="text-muted-foreground hover:text-primary font-medium">
            View all guides
          </Button>
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {posts.slice(0, 6).map((post, i) => (
          <Card key={post.id} data-index={i} className="flex flex-col h-full overflow-hidden border-border bg-card hover:shadow-md transition-all duration-300 rounded-md">
            {post.featuredImage && (
              <Link to={post.link || "#"} className="block overflow-hidden aspect-video">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </Link>
            )}
            <CardHeader className="flex-grow">
              {post.categories && post.categories.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {post.categories.slice(0, 1).map((cat, j) => (
                    <span key={cat.id} data-index={j} className="text-xs font-bold uppercase tracking-wider text-primary bg-secondary px-2.5 py-0.5 rounded-md">
                      {cat.name}
                    </span>
                  ))}
                </div>
              )}
              <Link to={post.link || "#"} className="group">
                <CardTitle className="font-default text-xl leading-tight group-hover:text-primary transition-colors line-clamp-2">
                  {post.title}
                </CardTitle>
              </Link>
              {post.excerpt && (
                <CardDescription className="mt-3 line-clamp-3 text-muted-foreground font-default">
                  <span dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                </CardDescription>
              )}
            </CardHeader>
            <CardFooter className="pt-0 text-sm text-muted-foreground flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.date ? new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent'}</span>
              </div>
              <Link to={post.link || "#"} className="text-primary font-bold hover:underline">
                Watch Now
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

function RelatedPosts({ currentPostType, currentPostId, currentCategoryId }: { currentPostType: string; currentPostId?: any; currentCategoryId?: any }) {
  
  const relatedQueryParams: any = { post_type: currentPostType, per_page: 6 };
  if (currentCategoryId) relatedQueryParams.cat = currentCategoryId;
  if (currentPostId) relatedQueryParams.exclude = currentPostId;
  const relatedWpQuery = new WP_Query(relatedQueryParams);

  return (
    <PostsProvider wp_query={relatedWpQuery}>
      <RelatedPostsConsumer />
    </PostsProvider>
  );
}

function SinglePostContent() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto animate-pulse">
        <div className="h-8 w-24 bg-muted/30 rounded-md mb-6" />
        <div className="h-12 md:h-16 bg-muted/30 rounded-md w-full mb-4" />
        <div className="h-12 md:h-16 bg-muted/30 rounded-md w-3/4 mb-8" />
        <div className="flex items-center gap-4 mb-12">
          <div className="w-12 h-12 rounded-md bg-muted/30" />
          <div className="space-y-2">
            <div className="h-4 w-32 bg-muted/30 rounded-md" />
            <div className="h-3 w-24 bg-muted/30 rounded-md" />
          </div>
        </div>
        <div className="w-full aspect-video bg-muted/30 rounded-md mb-12" />
        <div className="space-y-4">
          <div className="h-4 bg-muted/30 rounded-md w-full" />
          <div className="h-4 bg-muted/30 rounded-md w-full" />
          <div className="h-4 bg-muted/30 rounded-md w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-md bg-destructive/10 text-destructive mb-6">
          <Clock className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold font-default mb-4">Failed to load repair guide</h2>
        <p className="text-muted-foreground mb-8">{error}</p>
        <Button onClick={refetch} variant="outline" className="rounded-md">
          Try Again
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <h2 className="text-2xl font-bold font-default mb-4">Guide not found</h2>
        <p className="text-muted-foreground mb-8">The repair tutorial you are looking for is currently unavailable.</p>
        <Link to="#">
          <Button className="rounded-md">Return to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const currentWPQueryParams = getWvcClient().getCurrentWPQueryParams();
  const currentPostType = currentWPQueryParams.post_type || 'post';

  
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const videoMatch = post.content?.match(youtubeRegex);
  const videoUrl = videoMatch ? videoMatch[0] : null;

  
  const contentParts = post.content?.split("</p>");
  const firstParagraph = contentParts && contentParts.length > 0 ? contentParts[0] + "</p>" : "";
  const remainingContent = contentParts && contentParts.length > 1 ? contentParts.slice(1).join("</p>") : "";

  return (
    <article className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link to="/troubleshooting-guides/" className="inline-flex items-center text-sm font-bold uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Repair Guides
        </Link>
      </div>

      <header className="mb-10">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {post.categories.map((cat, i) => (
              <Link key={cat.id} to={cat.link || "#"} data-index={i}>
                <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-bold uppercase tracking-widest bg-secondary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default leading-tight mb-6 text-foreground">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-between gap-6 py-6 border-y border-border">
          <div className="flex items-center gap-4">
            <Avatar className="w-12 h-12 border border-border shadow-sm rounded-md">
              {post.author?.avatar?.['96'] ? (
                <AvatarImage src={post.author.avatar['96']} alt={post.author.name} className="rounded-md" />
              ) : (
                <AvatarFallback className="bg-secondary text-primary rounded-md">
                  <User className="w-5 h-5" />
                </AvatarFallback>
              )}
            </Avatar>
            <div>
              <div className="font-bold text-foreground font-default">
                {post.author?.name || 'No Filler Fixes Team'}
              </div>
              <div className="flex items-center text-sm text-muted-foreground mt-0.5 font-default">
                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                {post.date ? new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Recently published'}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="prose prose-lg max-w-none text-foreground/90 leading-relaxed font-default [&>p]:mb-6 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:font-serif [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-foreground [&>h2]:border-b [&>h2]:pb-2 [&>h2]:border-border [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:font-default [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:text-foreground [&>h4]:text-xl [&>h4]:font-bold [&>h4]:font-default [&>h4]:mt-6 [&>h4]:mb-4 [&>h4]:text-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:py-2 [&>blockquote]:italic [&>blockquote]:bg-secondary/30 [&>blockquote]:rounded-md [&>blockquote]:mb-6 [&>img]:rounded-md [&>img]:my-8 [&>img]:shadow-sm [&>a]:text-primary [&>a]:font-bold [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80 [&>pre]:bg-secondary [&>pre]:p-4 [&>pre]:rounded-md [&>pre]:overflow-x-auto [&>pre]:mb-6 [&>pre]:border [&>pre]:border-border [&>code]:bg-secondary [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded-md [&>code]:text-sm [&>code]:font-mono"
      >
        <div dangerouslySetInnerHTML={{ __html: firstParagraph }} />
        
        {videoUrl && <VideoEmbed videoUrl={videoUrl} />}
        
        <div dangerouslySetInnerHTML={{ __html: remainingContent }} />
      </div>

      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex items-center gap-2 mb-4 text-foreground font-bold uppercase text-xs tracking-widest">
            <Tag className="w-4 h-4" />
            <span>Repair Tags:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, i) => (
              <Link key={tag.id} to={tag.link || "#"} data-index={i}>
                <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-secondary hover:bg-primary hover:text-primary-foreground text-foreground transition-colors border border-border">
                  {tag.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Separator className="my-16 bg-border" />

      <RelatedPosts currentPostType={currentPostType} currentPostId={post.id} currentCategoryId={post.categories?.[0]?.id ?? post.categories?.[0]} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const wvcClient = getWvcClient();
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="240249" id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 min-h-screen">
      <div className="container mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>
      </div>
    </section>
  );
}